import type * as schemas from "@common/schemas/v2";
import * as models from "@common/models/v2";
import { StepActions } from "./step";
import { SimpleEventEmitter } from "@/lib/events/simpleEventEmitter";
import { EmptyPayload } from "@/lib/events/types";
import { QuestionChoice } from "./question";
import { ModalChoice } from "./modal";
import { FlowFlexibility } from "@common/schemas";
import { computed, makeObservable, observable, reaction, runInAction } from "mobx";

export type StepEvents = {
  initialized: EmptyPayload;
  started: EmptyPayload;
  finished: EmptyPayload;
  disposed: EmptyPayload;
  ran: EmptyPayload;
  questionAnswered: { choice: QuestionChoice };
  modalAnswered: { choice: ModalChoice };
  earlyCompletionForNavigation: { stepId: string };
};

export class BaseStep {
  id: string;
  text: string;
  start: number;
  end: number;
  attributes: models.StepAttributes;
  createdAt?: string;
  actions: StepActions;
  events: SimpleEventEmitter<StepEvents> = new SimpleEventEmitter();
  flowFlexibility: FlowFlexibility;
  isOnTrack = false;
  isProceededNext = false;
  isFinished = false;
  stopWatchingOnTrack?: () => void;
  stopProceedNextWhenUnblocked?: () => void;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    this.id = step.id;
    this.text = step.text;
    this.start = step.start;
    this.end = step.end;
    this.attributes = models.StepAttributesFactory.create(step.attributes);
    this.createdAt = step.createdAt;
    this.actions = actions;
    this.flowFlexibility = flowFlexibility;
    makeObservable(this, {
      isBlocking: computed,
      isOnTrack: observable,
      isFinished: observable,
    });
  }

  async init() {
    runInAction(() => {
      this.isFinished = false;
      this.isProceededNext = false;
    });

    this.events.emit("initialized", {});
    this.proceedNextWhenUnblocked();
    this.stopWatchingOnTrack?.();
    this.stopWatchingOnTrack = this.actions.onStepOnTrackChanged(this.id, (isOnTrack) => {
      runInAction(() => {
        this.isOnTrack = isOnTrack;
      });
    });
  }

  async run() {
    this.events.emit("ran", {});
  }

  async finish() {
    this.events.emit("finished", {});
    runInAction(() => {
      this.isFinished = true;
    });
  }

  private proceedNextWhenUnblocked() {
    this.stopProceedNextWhenUnblocked = reaction(
      () => [this.isBlocking, this.isFinished, this.isOnTrack],
      ([isBlocking, isFinishAchieved, isOnTrack]) => {
        if (!isBlocking && isOnTrack && (isFinishAchieved || this.skipStepOnEarlyUnblock)) {
          this.actions.proceedNext();
          this.stopProceedNextWhenUnblocked?.();
        }
      }
    );
  }

  dispose() {
    this.events.emit("disposed", {});
    this.stopWatchingOnTrack?.();
    this.stopProceedNextWhenUnblocked?.();
  }

  get additionalContent(): string | undefined {
    return undefined;
  }

  get prompt(): any {
    return null;
  }

  get isBlocking(): boolean {
    return this.isBlockingStepType && !this.isOnTrack;
  }

  get skipStepOnEarlyUnblock(): boolean {
    return false;
  }

  get isBlockingStepType(): boolean {
    return this.flowFlexibility === "strict";
  }

  get isTargetableStepType(): boolean {
    return false;
  }

  get isBlockingAndTargetableStepType(): boolean {
    return this.isBlockingStepType && this.isTargetableStepType;
  }

  get isAtom(): boolean {
    return this.start === this.end;
  }

  get assetUrls() {
    return this.attributes.assetUrls();
  }

  get emitter() {
    return this.events;
  }

  toJSON() {
    return null;
  }
}
